import { Button, Flex, Input, message } from "antd"
import { Form, Modal, Select } from "antd"
import { CountryCodes } from "../types/amazonAds"
import { memo, useEffect, useMemo, useState } from "react"
import { COUNTRY_CODES_ENUM } from "../constants/country.constants"
import { INewProject } from "../types/project"
import projectApi from "../api-wrapper/project/project"
import { useTypedSelector } from "../hooks/useTypedSelector"
import { useActions } from "../hooks/useActions"
import ProjectModalCampaigns from "./ProjectModalCampaigns"
const { TextArea } = Input

enum FormFields {
  MARKETPLACE = "marketplace",
  PROJECT_NAME = "projectName",
  ASINS = "asins",
  KEYWORDS = "keywords",
  CAMPAIGN = "campaigns",
  PRIMARY_ASIN = "primaryAsin",
}

interface ProjectModalProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  refetch?: () => void
  projectId?: string | null
  isEditModal?: boolean
}

const ProjectModal: React.FC<ProjectModalProps> = memo(function ProjectModal({
  isOpen,
  setIsOpen,
  refetch,
  projectId,
  isEditModal = false,
}) {
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false)
  const [form] = Form.useForm()

  const asins = Form.useWatch(FormFields.ASINS, form)

  const { projectData } = useTypedSelector((state) => state.projectDataSettings)
  const { campaigns, error } = useTypedSelector((state) => state.campaigns)
  const { fetchCampaignsList } = useActions()

  useEffect(() => {
    if (projectData && campaigns.length) {
      const campaignIds = campaigns
        .filter((campaign) => projectData.campaignIds.includes(Number(campaign.campaignId)))
        .map((campaign) => campaign.campaignId)
      form.setFieldsValue({
        marketplace: projectData.countryCode,
        projectName: projectData.title,
        asins:
          projectData.asins.length > 0
            ? projectData.asins.map((asin) => asin.asin).join(", ")
            : undefined,
        keywords: projectData.keywords.length > 0 ? projectData.keywords.join(", ") : undefined,
        campaigns: campaignIds,
        primaryAsin: projectData.asins.find((asin) => asin.isPrimary)?.asin,
      })
    }
  }, [projectData, campaigns])

  useEffect(() => {
    fetchCampaignsList()
  }, [])

  useEffect(() => {
    const isCreateModal = !isEditModal
    if (isCreateModal) {
      form.resetFields()
    }
  }, [isEditModal])

  useEffect(() => {
    if (error) {
      message.error("Error loading campaigns")
      setIsOpen(false)
    }
  }, [error])

  function handleOk() {
    setConfirmLoading(true)
    const values = form.getFieldsValue()
    if (!COUNTRY_CODES_ENUM.includes(values.marketplace)) {
      message.error("Invalid marketplace")
      return
    }

    const asins = values.asins?.split(",").map((asin: string) => asin.trim())
    const keywords = values.keywords?.split(",").map((keyword: string) => keyword.trim())

    const projectBody: INewProject = {
      countryCode: values.marketplace,
      title: values.projectName,
      asins,
      primaryAsin: values.primaryAsin,
      keywords,
      projectId: projectId ?? undefined,
      campaignIds: values.campaigns,
    }

    projectApi
      .createNewProjectData(projectBody)
      .then(() => {
        if (refetch) {
          refetch()
        }
      })
      .catch(() => {
        message.error("Failed to edit project")
      })

    setIsOpen(false)
    setConfirmLoading(false)
    if (!isEditModal) {
      form.resetFields()
    }
  }

  function handleCancel() {
    setIsOpen(false)
    if (!isEditModal) {
      form.resetFields()
    }
  }

  const selectPrimaryAsinOptions = useMemo(() => {
    return asins?.split(",").map((asin: string) => ({
      label: asin.trim(),
      value: asin.trim(),
    }))
  }, [asins])

  return (
    <Modal
      title="Create Project"
      open={isOpen}
      confirmLoading={confirmLoading}
      onCancel={handleCancel}
      footer={null}
    >
      <Form form={form} layout="vertical" onFinish={handleOk}>
        <Form.Item
          label="Marketplace"
          name={FormFields.MARKETPLACE}
          rules={[{ required: true, message: "Marketplace is required" }]}
          initialValue={CountryCodes.US}
        >
          <Select>
            <Select.Option value={CountryCodes.US}>US</Select.Option>
            <Select.Option value={CountryCodes.CA}>CA</Select.Option>
            <Select.Option value={CountryCodes.MX}>MX</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Project Name"
          name={FormFields.PROJECT_NAME}
          rules={[{ required: true, message: "Project Name is required" }]}
        >
          <Input placeholder="Enter name" />
        </Form.Item>

        <Form.Item
          label="ASINS"
          name={FormFields.ASINS}
          rules={[{ required: true, message: "ASINS is required" }]}
        >
          <TextArea placeholder="Enter asins (use comma to separate)" />
        </Form.Item>

        <Form.Item label="Primary ASIN" name={FormFields.PRIMARY_ASIN}>
          <Select options={selectPrimaryAsinOptions} />
        </Form.Item>

        <Form.Item label="Keywords" name={FormFields.KEYWORDS}>
          <TextArea placeholder="Enter keywords (use comma to separate)" />
        </Form.Item>
        <Form.Item label="Campaigns" name={FormFields.CAMPAIGN}>
          <ProjectModalCampaigns
            form={form}
            setCampaignsForm={(campaignIds) => {
              form.setFieldsValue({ [FormFields.CAMPAIGN]: campaignIds })
            }}
          />
        </Form.Item>

        <Form.Item>
          <Flex gap="small" justify="end">
            <Button htmlType="button" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              {projectId ? "Update" : "Create"}
            </Button>
          </Flex>
        </Form.Item>
      </Form>
    </Modal>
  )
})

export default ProjectModal
